import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, KeyRound, Phone, Mail } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(code.trim());
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Code d'accès invalide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left form panel */}
      <div className="w-full lg:w-[480px] xl:w-[520px] bg-white flex flex-col justify-between p-8 lg:p-12 order-2 lg:order-1">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="AMAL VBC" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-barlow font-bold text-xl tracking-tight">AMAL VBC</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">Plateforme administrative</div>
          </div>
        </div>

        <div className="my-auto py-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">
            Accès sécurisé
          </div>
          <h1 className="font-barlow font-bold text-4xl sm:text-5xl tracking-tight leading-none text-slate-900 mb-4">
            Connectez-vous
            <br />
            <span className="text-emerald-600">à votre espace.</span>
          </h1>
          <p className="text-slate-600 mb-10 leading-relaxed">
            Saisissez votre code d'accès personnel pour gérer l'ensemble des activités du club AMAL VBC.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="code" className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Code d'accès
              </Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="code"
                  data-testid="login-code-input"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="AMAL-ADMIN-2026"
                  autoComplete="off"
                  autoFocus
                  required
                  className="pl-10 h-12 font-mono tracking-wider border-slate-300 focus:border-emerald-600 focus:ring-emerald-600"
                />
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Le code est délivré par l'administrateur du club. Il est strictement personnel.
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading || !code.trim()}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold uppercase tracking-wider text-sm rounded-md"
            >
              {loading ? "Connexion..." : "Accéder à la plateforme"}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
            Connexion chiffrée • Aucun mot de passe stocké
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6 text-xs text-slate-500 space-y-1">
          <div className="flex items-center gap-2">
            <Mail className="h-3 w-3" /> amalvbc@yahoo.com
          </div>
          <div className="flex items-center gap-2">
            <Phone className="h-3 w-3" /> +229 01 97 57 95 82
          </div>
          <div className="pt-2">© {new Date().getFullYear()} AMAL VBC — Tous droits réservés</div>
        </div>
      </div>

      {/* Right hero panel */}
      <div className="hidden lg:flex flex-1 login-bg relative order-1 lg:order-2 grain">
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex justify-end">
            <div className="text-xs font-bold uppercase tracking-[0.25em] border border-emerald-400/40 text-emerald-300 px-3 py-1.5 rounded-full bg-emerald-900/20 backdrop-blur-sm">
              Saison 2025 — 2026
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-400 mb-4">
              Club AMAL VBC
            </div>
            <div className="font-barlow font-bold text-5xl xl:text-6xl leading-none tracking-tighter mb-6">
              Excellence,
              <br />
              discipline,
              <br />
              <span className="text-emerald-400">esprit d'équipe.</span>
            </div>
            <div className="text-slate-300 max-w-md leading-relaxed">
              La plateforme officielle de gestion du club — équipes, transferts, finances, personnel et documents officiels, centralisés en un seul endroit.
            </div>

            <div className="mt-12 grid grid-cols-4 gap-6 border-t border-white/15 pt-6 max-w-xl">
              {[
                { v: "4", l: "Équipes" },
                { v: "100%", l: "Sécurisé" },
                { v: "12+", l: "Documents officiels" },
                { v: "9", l: "Rôles utilisateurs" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-barlow font-bold text-3xl text-white">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
